import { Alert, CircularProgress, Fade, Icon, IconButton, InputAdornment } from "@mui/material";
import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { useEffect, useState } from "react";
import SourceActions from "redux-react/actions/sourceActions";
import { useDispatch } from "react-redux";

function decodeHtml(html) {
	const doc = new DOMParser().parseFromString(html, "text/html");
	return doc.documentElement.textContent;
}

/**
 * Component to import a wordpress page
 * @param {*} param0
 * @returns {JSX.Element} - WebImport
 */
const WordpressPageImport = ({ onFilesSelection, validStep }) => {
	const dispatch = useDispatch();

	const [loadingProgress, setLoadingProgress] = useState(false);

	const [url, setUrl] = useState("");
	const [urlError, setUrlError] = useState(null);

	const [selectedType, setSelectedType] = useState(null);
	const [types, setTypes] = useState([]);

	const STEPS = {
		IMPORT: "import",
		TYPE_SELECT: "type_select"
	};

	const [currentStep, setCurrentStep] = useState(STEPS.IMPORT);

	const formatResult = res => {
		const formattedFiles = (res || []).map(item => {
			return {
				name: decodeHtml(item.title.rendered) || t("ASSETS.WORDPRESS.unknowTitle"),
				url: item?.link,
				content: decodeHtml(item.content.rendered) || "",
				image: (item?.yoast_head_json?.og_image ?? [])[0]?.url,
				alreadyExists: false
			};
		});
		return formattedFiles;
	};

	/**
	 * Download file from URL
	 */
	const getWordpressTypes = async () => {
		setUrlError(null);
		setLoadingProgress(true);

		try {
			const urlObj = new URL(url);
			// take only http + domain
			const cleanUrl = urlObj.protocol + "//" + urlObj.hostname;

			dispatch(
				SourceActions.getIntegrationWordpress(
					cleanUrl,
					{
						type: "types"
					},
					res => {
						setLoadingProgress(false);
						setTypes(res || []);
						if (res.length === 0) {
							setUrlError(t("ASSETS.WORDPRESS.noTypesFound"));
						} else {
							setCurrentStep(STEPS.TYPE_SELECT);
						}
					},
					err => {
						setLoadingProgress(false);
						setUrlError(t("SOURCES.UPLOAD.urlError"));
					}
				)
			);
		} catch (err) {
			setLoadingProgress(false);
			setUrlError(t("ASSETS.WORDPRESS.invalidURL"));
		}
	};

	/**
	 * Download file from URL
	 */
	const getWordpressContent = async type => {
		setUrlError(null);
		setLoadingProgress(true);

		try {
			const urlObj = new URL(url);
			// take only http + domain
			const cleanUrl = urlObj.protocol + "//" + urlObj.hostname;

			dispatch(
				SourceActions.getIntegrationWordpress(
					cleanUrl,
					{
						type: `${type}?_fields=content.rendered,title.rendered,link,yoast_head_json`
					},
					res => {
						if (res.length === 0) {
							setUrlError(t("ASSETS.WORDPRESS.noElementFoundWithType"));
							setLoadingProgress(false);
						} else {
							const formattedFiles = formatResult(res);
							setLoadingProgress(false);
							onFilesSelection(formattedFiles);
						}
					},
					err => {
						setLoadingProgress(false);
						setUrlError(t("ASSETS.WORDPRESS.cannotFetchElementsWithType"));
					}
				)
			);
		} catch (err) {
			setLoadingProgress(false);
			setUrlError(t("ASSETS.WORDPRESS.invalidURL"));
		}
	};

	const getContent = () => {
		switch (currentStep) {
			case STEPS.IMPORT: {
				return (
					<MDBox sx={{ width: "70%" }}>
						<MDTypography variant="h4">{t("ASSETS.WORDPRESS.addUrlTitle")}</MDTypography>
						<MDTypography variant="subtitle2">
							{t("ASSETS.WORDPRESS.addUrlDescription")}
						</MDTypography>

						<MDInput
							autoFocus
							sx={{ mt: 5 }}
							disabled={loadingProgress}
							value={url}
							className="dialogInput"
							placeholder={t("SOURCES.UPLOAD.urlPlaceholder")}
							onChange={e => {
								setUrlError("");
								setUrl(e.target.value);
							}}
							onKeyDown={e => {
								if (e.key === "Enter") {
									getWordpressTypes();
								}
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{!loadingProgress && (
											<IconButton disabled={Boolean(!url)} color="info" onClick={getWordpressTypes}>
												<Icon>search</Icon>
											</IconButton>
										)}
										{loadingProgress && <CircularProgress color="info" size={20} />}
									</InputAdornment>
								)
							}}
						/>
						{/* Loading */}
						{loadingProgress && (
							<Fade in={loadingProgress} timeout={300}>
								<MDBox sx={{ mt: 3 }}>
									<Alert
										icon={
											<CircularProgress
												size={25}
												sx={{ marginRight: 2 }}
												thickness={5}
												color="info"
											/>
										}
										severity="info"
									>
										<MDTypography variant="h6" fontSize="small" color="info">
											{t("SOURCES.URL.loadingUrl")}
										</MDTypography>
									</Alert>
								</MDBox>
							</Fade>
						)}
						{/* Error */}
						{urlError && (
							<Fade in={!lod_.isNil(urlError) && !lod_.isEmpty(urlError)} timeout={300}>
								<MDBox sx={{ mt: 3 }}>
									<Alert icon={<Icon color="error">close</Icon>} severity="error">
										<MDTypography variant="h6" fontSize="small" color="error">
											{urlError}
										</MDTypography>
									</Alert>
								</MDBox>
							</Fade>
						)}
					</MDBox>
				);
			}
			case STEPS.TYPE_SELECT: {
				return (
					<MDBox sx={{ width: "70%" }}>
						<MDBox display="flex" alignItems="center" gap={2}>
							<IconButton
								onClick={() => {
									setCurrentStep(STEPS.IMPORT);
									setTypes([]);
									setSelectedType(null);
								}}
							>
								<Icon fontSize="large" color="info">
									arrow_back
								</Icon>
							</IconButton>
							<MDBox>
								<MDTypography variant="h4">{t("ASSETS.WORDPRESS.choseTypeTitle")}</MDTypography>
								<MDTypography variant="subtitle2">
									{t("ASSETS.WORDPRESS.choseTypeDescription")}
								</MDTypography>
							</MDBox>
						</MDBox>

						{/* Error */}
						{urlError && (
							<Fade in={!lod_.isNil(urlError) && !lod_.isEmpty(urlError)} timeout={300}>
								<MDBox sx={{ mt: 3 }}>
									<Alert icon={<Icon color="error">close</Icon>} severity="error">
										<MDTypography variant="h6" fontSize="small" color="error">
											{urlError}
										</MDTypography>
									</Alert>
								</MDBox>
							</Fade>
						)}

						<MDBox>
							{types.map(type => (
								<MDBox
									key={type.rest_base}
									sx={{
										mt: 3,
										p: 2,
										border: "1px solid",
										borderColor: "grey.300",
										borderRadius: 2,
										cursor: "pointer",
										"&:hover": {
											backgroundColor: "grey.100"
										},
										backgroundColor: loadingProgress ? "grey.100" : "transparent"
									}}
									onClick={() => {
										if (!loadingProgress) {
											setSelectedType(type.rest_base);
											getWordpressContent(type.rest_base);
										}
									}}
									display="flex"
									alignItems="center"
									justifyContent="space-between"
								>
									<MDBox>
										<MDTypography variant="h6">{decodeHtml(type.name)}</MDTypography>
										<MDTypography variant="button">{decodeHtml(type.description)}</MDTypography>
										<MDTypography variant="body2" color="text">
											Importer les éléments de type {decodeHtml(type.name)}
										</MDTypography>
									</MDBox>

									<CircularProgress
										size={20}
										sx={{ marginLeft: 2 }}
										thickness={5}
										color="info"
										style={{
											visibility:
												loadingProgress && selectedType === type.rest_base ? "visible" : "hidden"
										}}
									/>
								</MDBox>
							))}
						</MDBox>
					</MDBox>
				);
			}
			default: {
				return null;
			}
		}
	};

	useEffect(() => {
		setCurrentStep(STEPS.IMPORT);
		setUrl("");
		setUrlError(null);
		setLoadingProgress(false);
		setTypes([]);
		setSelectedType(null);
	}, []);

	return (
		<MDBox mt={5} display="flex" flex="1" flexDirection="row" justifyContent="center">
			{getContent()}
		</MDBox>
	);
};

export default WordpressPageImport;
